import React, { useState, useEffect, useRef } from 'react';
import { Spinner, Button, TextArea, Icon } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import api from '../../services/api';
import { formatDistanceToNow } from 'date-fns';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import './Comments.css';

interface Comment {
  id: string;
  text: string;
  userId: string;
  userName?: string;
  createdAt: string;
  updatedAt?: string;
}

interface CommentsProps {
  nodeId?: string;  // For backward compatibility
  entityId?: string;  // New prop for any entity
  type?: 'mindmap' | 'ticket';  // Entity type
  selectedCommentId?: string;
  nodeLabel?: string;
}

/**
 * Modern comments component with clean design and smooth interactions
 * Features:
 * - Inline composer with focus state
 * - Avatar initials for users
 * - Relative time display
 * - Smooth hover effects
 * - Minimal borders and clutter
 */
const Comments: React.FC<CommentsProps> = (props) => {
  const { nodeId, entityId, type = 'mindmap', selectedCommentId, nodeLabel } = props;
  const actualEntityId = entityId || nodeId;  // Use entityId if provided, otherwise use nodeId
  const appStore = useAppStore();
  const { mindmap, auth } = appStore;
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  
  // Load comments
  useEffect(() => {
    if (actualEntityId) {
      loadComments();
    }
  }, [actualEntityId, type]);
  
  const loadComments = async () => {
    try {
      setIsLoading(true);
      // Auth is handled via httpOnly cookies
      
      // Determine the API endpoint based on the entity type
      let endpoint = '';
      if (type === 'ticket') {
        endpoint = `/api/tickets/${actualEntityId}/comments`;
      } else {
        endpoint = `/mindmaps/nodes/${actualEntityId}/comments`;
      }
      
      const response = await api.get(endpoint, { 
        withCredentials: true 
      });
      
      setComments(response.data || []);
    } catch (error) {
      console.error('Error loading comments:', error);
      setComments([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * Parse mentions from comment text
   * Looks for @username patterns
   */
  const parseMentions = (text: string) => {
    const mentionRegex = /@(\w+)/g;
    const mentions = [];
    let match;
    
    while ((match = mentionRegex.exec(text)) !== null) {
      mentions.push({
        type: 'person' as const,
        id: match[1], // In a real app, we'd look up the user ID from username
        label: match[1]
      });
    }
    
    return mentions;
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim() || !actualEntityId) return;
    
    try {
      setIsSubmitting(true);
      // Auth is handled via httpOnly cookies
      
      // Parse mentions from comment text
      const mentions = parseMentions(newComment);
      
      // Determine the API endpoint based on the entity type
      let endpoint = '';
      if (type === 'ticket') {
        endpoint = `/api/tickets/${actualEntityId}/comments`;
      } else {
        endpoint = `/mindmaps/nodes/${actualEntityId}/comments`;
      }
      
      // Include mentions in the POST request so backend can send Slack notifications
      const response = await api.post(
        endpoint,
        { 
          text: newComment,
          mentions: mentions.length > 0 ? mentions : undefined,
        },
        { withCredentials: true }
      );
      
      // Add the new comment to the list
      const newCommentData = response.data;
      setComments([...comments, newCommentData]);
      
      setNewComment('');
      setIsFocused(false);
      textAreaRef.current?.blur();
    } catch (error) {
      console.error('Error posting comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleDeleteComment = async (commentId: string) => {
    try {
      // Auth is handled via httpOnly cookies
      
      await api.delete(
        `/mindmaps/comments/${commentId}`,
        { withCredentials: true }
      );
      
      // Remove from list
      setComments(comments.filter(c => c.id !== commentId));
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmitComment();
    }
  };

  // Get initials from username
  const getInitials = (name?: string) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Format relative time
  const formatTime = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'recently';
    }
  };
  
  if (isLoading) {
    return (
      <div className="comments-container-modern">
        <div className="comments-loading">
          <Spinner size={20} />
        </div>
      </div>
    );
  }
  
  return (
    <div className="comments-container-modern">
      {/* Comment list */}
      <div className="comments-list-modern">
        {comments.length === 0 ? (
          <div className="comments-empty">
            <Icon icon={IconNames.CHAT} size={24} className="empty-icon" />
            <span className="empty-text">No comments yet</span>
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="comment-item">
              <div className="comment-avatar">
                {getInitials(comment.userName)}
              </div>
              <div className="comment-content">
                <div className="comment-header-modern">
                  <span className="comment-author">{comment.userName || 'Anonymous'}</span>
                  <span className="comment-time">{formatTime(comment.createdAt)}</span>
                  <Button
                    icon={IconNames.TRASH}
                    minimal
                    small
                    className="comment-delete"
                    onClick={() => handleDeleteComment(comment.id)}
                  />
                </div>
                <div className="comment-text-modern">{comment.text}</div>
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* Inline composer */}
      <div className={`comment-composer-modern ${isFocused ? 'focused' : ''}`}>
        <TextArea
          inputRef={textAreaRef}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            // Delay blur to allow button click
            setTimeout(() => {
              if (!newComment.trim()) setIsFocused(false);
            }, 150);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Add a comment..."
          className="composer-input"
          growVertically
          fill
        />
        {(isFocused || newComment.trim()) && (
          <div className="composer-actions">
            <span className="composer-hint">⌘ + Enter to send</span>
            <Button
              intent="primary"
              text="Comment"
              small
              onClick={handleSubmitComment}
              loading={isSubmitting}
              disabled={!newComment.trim()}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Comments;